import type { Forecast, HeroId, Stance, ZoneId } from '@fukemon/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type GameStore = {
  ageAccepted: boolean;
  soundOn: boolean;
  reducedEffects: boolean;
  selectedHero: HeroId;
  zone: ZoneId;
  activePanel: 'none' | 'market' | 'raid' | 'vault' | 'arcade' | 'settings';
  forecast?: Forecast;
  xp: number;
  raidDamage: number;
  combat: { hp: number; maxHp: number; kills: number; wave: number; attackQueued: number };
  movement: { x: number; z: number };
  setAgeAccepted: (accepted: boolean) => void;
  setSoundOn: (on: boolean) => void;
  setReducedEffects: (reduced: boolean) => void;
  selectHero: (hero: HeroId) => void;
  setZone: (zone: ZoneId) => void;
  setPanel: (panel: GameStore['activePanel']) => void;
  setMovement: (movement: { x: number; z: number }) => void;
  queueAttack: () => void;
  setCombat: (combat: Partial<GameStore['combat']>) => void;
  submitLocalForecast: (
    player: `0x${string}`,
    roundId: number,
    dayId: number,
    assetId: number,
    stance: Stance
  ) => void;
};

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ageAccepted: false,
      soundOn: false,
      reducedEffects: false,
      selectedHero: 'cuntmander',
      zone: 'exchange',
      activePanel: 'none',
      xp: 120,
      raidDamage: 12_480,
      combat: { hp: 100, maxHp: 100, kills: 0, wave: 1, attackQueued: 0 },
      movement: { x: 0, z: 0 },
      setAgeAccepted: (ageAccepted) => set({ ageAccepted }),
      setSoundOn: (soundOn) => set({ soundOn }),
      setReducedEffects: (reducedEffects) => set({ reducedEffects }),
      selectHero: (selectedHero) => set({ selectedHero }),
      setZone: (zone) => set({ zone }),
      setPanel: (activePanel) => set({ activePanel }),
      setMovement: (movement) => set({ movement }),
      queueAttack: () =>
        set((state) => ({
          combat: { ...state.combat, attackQueued: state.combat.attackQueued + 1 }
        })),
      setCombat: (combat) =>
        set((state) => ({ combat: { ...state.combat, ...combat } })),
      submitLocalForecast: (player, roundId, dayId, assetId, stance) =>
        set({
          forecast: {
            player,
            roundId,
            dayId,
            assetId,
            stance,
            heroId: get().selectedHero,
            nonce: Date.now(),
            deadline: Math.floor(Date.now() / 1000) + 600
          }
        })
    }),
    {
      name: 'fukemon-market-mayhem-v1',
      partialize: (state) => ({
        ageAccepted: state.ageAccepted,
        soundOn: state.soundOn,
        reducedEffects: state.reducedEffects,
        selectedHero: state.selectedHero,
        forecast: state.forecast,
        xp: state.xp
      })
    }
  )
);
